import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/** One audit event returned by a microservice. `resource` is the canonical
 * diff/full payload; portal shows it as pretty-printed JSON in the modal. */
export interface AuditEvent {
  resource?: unknown;
  timestamp?: string;
  userName?: string;
  event?: string;
  [key: string]: unknown;
}

export interface AuditResponse {
  events?: AuditEvent[];
  [key: string]: unknown;
}

/** Parse the auditUrl from a history log entry into `{microservice, path}`.
 * Mirrors portal HistoryView's inline parseAuditUrl. The microservice is
 * whatever path segment precedes `v1/`, e.g.
 * "/api/order/v1/orders/X/auditLogs?..." → { microservice: "order", path: "v1/orders/X/auditLogs?..." } */
export function parseAuditUrl(url: string): { microservice: string; path: string } {
  const match = url.match(/\/([^/]+)\/(v\d+\/.*$)/);
  if (!match) return { microservice: '', path: url };
  return { microservice: match[1] ?? '', path: match[2] ?? url };
}

/** Audit microservices whose log endpoint is super-admin only. A "View Log"
 * link pointing at one of these is hidden from non-super-admins so they don't
 * click into a wall. Add a service here to gate its link the same way. */
export const SUPER_ADMIN_ONLY_AUDIT_SERVICES = new Set(['transformation']);

/** Whether the current user may open the audit log behind `auditUrl`. False
 * when there is no url, or the url points at a super-admin-only service and the
 * user is not a super-admin. Uses the same microservice discriminator that
 * routes the click, so the link and the click never disagree. */
export function canOpenAuditLog(
  auditUrl: string | null | undefined,
  isSuperAdmin: boolean,
): boolean {
  if (!auditUrl) return false;
  if (isSuperAdmin) return true;
  return !SUPER_ADMIN_ONLY_AUDIT_SERVICES.has(parseAuditUrl(auditUrl).microservice);
}

/** Apply portal's per-microservice URL massaging. Each service's `getAuditInfo`
 * rewrites the path differently (see portal/.../services/{order,shipment,...}Services.js). */
function rewritePath(microservice: string, path: string): string {
  let next = path;
  switch (microservice) {
    case 'order':
      next = next.replace(/^.*orders/, 'v1/orders');
      if (!next.includes('/auditLogs')) next = next.replace('?', '/auditLogs?');
      next = next.replace('DIFF', 'FULL');
      break;
    case 'shipment':
      next = next.replace('DIFF', 'FULL');
      break;
    case 'transformation':
      // No rewriting — portal calls the URL as-is.
      break;
    case 'message':
      // No rewriting — portal calls the URL as-is.
      break;
    case 'vendor':
      // Mirrors legacy `vendorServices.getAuditInfo`:
      // - normalize to v1/vendors/... or v1/vendorProducts/...
      // - inject /auditLogs before the query string when missing
      // - request FULL events not DIFF
      next = next.replace(/^.*vendors/, 'v1/vendors');
      next = next.replace(/^.*vendorProducts/, 'v1/vendorProducts');
      if (!next.includes('/auditLogs')) next = next.replace('?', '/auditLogs?');
      next = next.replaceAll('DIFF', 'FULL');
      break;
    default:
      break;
  }
  return next;
}

/**
 * Audit-log fetcher. Each known microservice has its own endpoint so the
 * service-prefix lands in the right place; we never let the caller pass a
 * full URL because the proxy needs the leading `/order` / `/shipment` / etc.
 * - order audit logs → /order/<rewritten path>
 * - shipment audit logs → /shipment/<rewritten path>
 * - transformation logs → /transformation/<path>
 * - message audit logs → /notification/<path>
 * - routing logs → /routing/v1/logs/{orderId} (separate API shape)
 */
export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: portalBaseQuery,
  tagTypes: [],
  endpoints: (builder) => ({
    getOrderAuditInfo: builder.query<AuditResponse, string>({
      query: (path) => ({
        url: `${services.order}/${rewritePath('order', path)}`,
        method: 'GET',
      }),
    }),
    getShipmentAuditInfo: builder.query<AuditResponse, string>({
      query: (path) => ({
        url: `${services.shipment}/${rewritePath('shipment', path)}`,
        method: 'GET',
      }),
    }),
    getMessagesAuditInfo: builder.query<AuditResponse, string>({
      query: (path) => ({
        url: `${services.notification}/${rewritePath('message', path)}`,
        method: 'GET',
      }),
    }),
    getTransformationAuditInfo: builder.query<AuditResponse, string>({
      query: (path) => ({
        url: `${services.transformation}/${rewritePath('transformation', path)}`,
        method: 'GET',
      }),
    }),
    getRoutingLogs: builder.query<unknown, string>({
      query: (orderId) => ({
        url: `${services.routing}/v1/logs/${orderId}`,
        method: 'GET',
      }),
    }),
    /**
     * Vendor / facility / vendor-product audit info. The caller passes a
     * raw `auditUrl` from a history-log row; `rewritePath('vendor', ...)`
     * normalizes it to a v1/vendors or v1/vendorProducts path with
     * /auditLogs appended and the DIFF→FULL flip applied.
     */
    getVendorAuditInfo: builder.query<AuditResponse, string>({
      query: (path) => ({
        url: `${services.vendor}/${rewritePath('vendor', path)}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLazyGetOrderAuditInfoQuery,
  useLazyGetShipmentAuditInfoQuery,
  useLazyGetMessagesAuditInfoQuery,
  useLazyGetTransformationAuditInfoQuery,
  useLazyGetRoutingLogsQuery,
  // Non-lazy variant — the Orders V2 Inspector lifecycle fetches routing logs
  // on open to timestamp the "Routed to vendor" stage.
  useGetRoutingLogsQuery,
  useLazyGetVendorAuditInfoQuery,
} = auditApi;
